/**
 * Extract the server's real error message from a failed
 * `supabase.functions.invoke()` call.
 *
 * supabase-js throws a FunctionsHttpError whose `.message` is the generic
 * "Edge Function returned a non-2xx status code" — useless in a toast and in
 * logs (the Ultra Europe schedule-save incident surfaced only that string
 * while the actual failure was a duplicate-key violation). Our edge functions
 * always return `{ "error": "<reason>" }` on failure; this helper reads that
 * body off the error's `.context` Response and falls back to the generic
 * message when the body is missing or unreadable.
 *
 * Some functions answer with both a machine code and a sentence —
 * `{ "error": "NOT_FOUND", "message": "No user with that id" }`. The sentence
 * wins when it is there: "NOT_FOUND" in a toast is barely better than the
 * generic string this helper exists to replace.
 */
export async function invokeErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error && error.message ? error.message : "Unknown error";
  if (!error || typeof error !== "object") return fallback;

  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) return fallback;

  try {
    // Clone so a caller can still consume the response elsewhere.
    const body = await context.clone().json();
    if (body && typeof body.message === "string" && body.message.length > 0) {
      return body.message;
    }
    if (body && typeof body.error === "string" && body.error.length > 0) {
      return body.error;
    }
  } catch {
    // Non-JSON body — keep the fallback.
  }
  return fallback;
}
